import type { RefObject } from "react";
import type { WorkflowOut } from "../types";
import { CompareDefaultDialog } from "./compare-default-dialog";
import { DuplicateDialog } from "./duplicate-dialog";
import { PauseDefaultDialog } from "./pause-default-dialog";
import { RestoreDefaultDialog } from "./restore-default-dialog";
import { UpgradeSheet } from "./upgrade-prompts";

export type DefaultDialog = "pause" | "restore" | "compare" | "duplicate" | "upgrade";

/** Gives focus back to the control that opened a dialog from a menu (the menu item itself is gone by then). */
export function focusBack(target: RefObject<HTMLElement | null>): (event: Event) => void {
  return (event) => {
    event.preventDefault();
    target.current?.focus();
  };
}

interface DefaultDialogsProps {
  workflow: WorkflowOut;
  open: DefaultDialog | null;
  /** The control to focus when a dialog closes. */
  opener: RefObject<HTMLElement | null>;
  pausing: boolean;
  onPause: () => void;
  onClose: () => void;
}

/** The dialogs the actions of a default open from the list; the builder wires the same dialogs to its own state. */
export function DefaultDialogs({ workflow, open, opener, pausing, onPause, onClose }: DefaultDialogsProps) {
  const returnFocus = focusBack(opener);
  if (open === "pause") return <PauseDefaultDialog workflow={workflow} busy={pausing} onConfirm={onPause} onClose={onClose} onCloseAutoFocus={returnFocus} />;
  if (open === "restore") return <RestoreDefaultDialog workflow={workflow} onRestored={onClose} onClose={onClose} onCloseAutoFocus={returnFocus} />;
  if (open === "compare") return <CompareDefaultDialog workflowId={workflow.id} onClose={onClose} onCloseAutoFocus={returnFocus} />;
  if (open === "duplicate") return <DuplicateDialog workflow={workflow} onClose={onClose} onCloseAutoFocus={returnFocus} />;
  if (open === "upgrade") return <UpgradeSheet onClose={onClose} onCloseAutoFocus={returnFocus} />;
  return null;
}
